import React, { useState, useEffect, useCallback, useRef } from "react";
import { DialogLayout, Button, TextField } from "../../../../ui";
import { FeatherSearch } from "@subframe/core";
import { VendorBill } from "../../../../types";
import { getOutstandingBillsForVendor } from "../../../../services/vendorApi";
import { useToast } from "../../../../shared/presentation/components";

interface ViewOutstandingBillsModalProps {
  open: boolean;
  vendorId: string;
  vendorName: string;
  onOpenChange: (open: boolean) => void;
}

const ViewOutstandingBillsModal: React.FC<ViewOutstandingBillsModalProps> = ({
  open,
  vendorId,
  vendorName,
  onOpenChange,
}) => {
  const { showError } = useToast();

  // State for bills
  const [loadingBills, setLoadingBills] = useState(false);
  const [billsOffset, setBillsOffset] = useState(0);
  const [billsHasMore, setBillsHasMore] = useState(false);
  const [billsLoading, setBillsLoading] = useState(false);
  const [billSearchTerm, setBillSearchTerm] = useState<string>("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // State for bills (read-only display)
  const [bills, setBills] = useState<VendorBill[]>([]);

  // Search timeout ref for debouncing
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevSearchTermRef = useRef<string>("");

  // Helper function to convert API response to VendorBill
  const convertToVendorBill = (item: any): VendorBill => {
    return {
      bill_id: item.bill_id,
      bill_number: item.bill_number || item.bill_id,
      vendor_id: item.vendor_id,
      vendor_name: item.vendor_name,
      exchange_rate: item.exchange_rate || 1,
      total: item.total,
      balance: item.balance,
      tds_total: item.tds_total || 0,
      currency_code: item.currency_code,
      currency_symbol: item.currency_symbol,
      status: item.status,
    };
  };

  // Fetch outstanding bills
  const fetchOutstandingBills = useCallback(
    async (reset: boolean = true) => {
      if (reset) {
        setBillsOffset(0);
        setIsLoadingMore(false);
        setLoadingBills(true);
      } else {
        setIsLoadingMore(true);
        setBillsLoading(true);
      }

      try {
        const result = await getOutstandingBillsForVendor(
          vendorId,
          20,
          reset ? 0 : billsOffset,
          billSearchTerm || undefined
        );

        // Convert to VendorBill format
        const fetchedBills = result.data.map(convertToVendorBill);

        setBillsHasMore(result.pagination.has_more);
        setBillsOffset(result.pagination.offset + result.pagination.limit);

        if (reset) {
          setBills(fetchedBills);
        } else {
          setBills((prev) => [...prev, ...fetchedBills]);
        }
      } catch (error) {
        console.error("Failed to fetch outstanding bills:", error);
        showError("Failed to fetch outstanding bills. Please try again.");
        if (reset) {
          setBills([]);
        }
      } finally {
        if (reset) {
          setLoadingBills(false);
        } else {
          setIsLoadingMore(false);
          setBillsLoading(false);
        }
      }
    },
    [vendorId, billsOffset, billSearchTerm, showError]
  );

  const loadMoreBills = useCallback(() => {
    if (!billsHasMore || isLoadingMore || loadingBills) return;
    fetchOutstandingBills(false);
  }, [billsHasMore, isLoadingMore, loadingBills, fetchOutstandingBills]);

  // Fetch bills when modal opens
  useEffect(() => {
    if (open) {
      fetchOutstandingBills(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search effect
  useEffect(() => {
    if (!open) return;

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const currentSearchTerm = billSearchTerm.trim();
    const previousSearchTerm = prevSearchTermRef.current.trim();

    // If search term is empty and it was previously non-empty, refetch immediately
    if (!currentSearchTerm && previousSearchTerm) {
      prevSearchTermRef.current = currentSearchTerm;
      setBillsOffset(0);
      fetchOutstandingBills(true);
      return;
    }

    // If search term has value, debounce the search
    if (currentSearchTerm) {
      prevSearchTermRef.current = currentSearchTerm;
      searchTimeoutRef.current = setTimeout(() => {
        setBillsOffset(0);
        fetchOutstandingBills(true);
      }, 300);
    } else {
      prevSearchTermRef.current = currentSearchTerm;
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billSearchTerm, open]);

  const handleClose = () => {
    setBills([]);
    setBillSearchTerm("");
    setBillsOffset(0);
    setIsLoadingMore(false);
    onOpenChange(false);
  };

  // Format amount helper
  const formatAmount = (amount: number): string => {
    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <DialogLayout open={open} onOpenChange={handleClose}>
      <div className="flex w-256 flex-col items-start gap-6 bg-default-background px-8 py-6">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Outstanding Bills - {vendorName}
        </span>

        <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-neutral-50 px-8 py-6 shadow-sm">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {vendorName}
            </span>
          </div>
        </div>

        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Outstanding Bills
            </span>
            <div className="flex items-center gap-2">
              <TextField
                className="h-8 w-48"
                label=""
                helpText=""
                icon={<FeatherSearch />}
              >
                <TextField.Input
                  placeholder="Search bills"
                  value={billSearchTerm}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setBillSearchTerm(event.target.value);
                  }}
                />
              </TextField>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Available Bills
                {loadingBills && (
                  <span className="text-body font-body text-neutral-500 ml-2">
                    (Loading...)
                  </span>
                )}
              </span>
            </div>
            <div className="flex w-full flex-col items-center gap-3 px-4">
              {bills.length > 0 ? (
                bills.map((bill) => {
                  const paid = bill.total - bill.balance;
                  return (
                    <div
                      key={bill.bill_id}
                      className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background px-4 py-3"
                    >
                      <div className="flex flex-col items-start flex-shrink-0">
                        <span className="text-body-bold font-body-bold text-default-font">
                          {bill.bill_number || bill.bill_id}
                        </span>
                        <span className="text-body font-body text-neutral-500">
                          {bill.vendor_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-8 flex-shrink-0">
                        <div className="flex flex-col items-center w-32">
                          <span className="text-caption font-caption text-neutral-400">
                            Full
                          </span>
                          <span className="text-body font-body text-default-font">
                            {bill.currency_symbol} {formatAmount(bill.total)}
                          </span>
                        </div>
                        <div className="flex flex-col items-center w-32">
                          <span className="text-caption font-caption text-neutral-400">
                            Paid
                          </span>
                          <span className="text-body font-body text-default-font">
                            {bill.currency_symbol} {formatAmount(paid)}
                          </span>
                        </div>
                        <div className="flex flex-col items-center w-32">
                          <span className="text-caption font-caption text-neutral-400">
                            Remaining
                          </span>
                          <span className="text-body font-body text-default-font">
                            {bill.currency_symbol} {formatAmount(bill.balance)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : !loadingBills ? (
                <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
                  <span className="text-body font-body text-neutral-500">
                    No outstanding bills found for {vendorName}
                  </span>
                </div>
              ) : null}
              {billsHasMore && (
                <div className="flex w-full items-center justify-center pt-2">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={loadMoreBills}
                    disabled={isLoadingMore || loadingBills}
                    loading={isLoadingMore}
                  >
                    {isLoadingMore ? "Loading..." : "Load More Bills"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-end border-t border-solid border-neutral-border pt-6">
          <Button variant="neutral-tertiary" onClick={handleClose}>
            Close
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default ViewOutstandingBillsModal;
